import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

import { ProductStocksReport_productStocks_edges_node } from "../../types/ProductStocksReport";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "ProductStockList" }
);

const formatter = new Intl.NumberFormat("en-IN", {
  currency: "PHP",
  currencyDisplay: "symbol",
  minimumFractionDigits: 2,
  style: "currency"
});

export interface ProductStockListProps extends ListProps {
  productStocks: ProductStocksReport_productStocks_edges_node[];
  onProductStockRowClick?: (id: string, variantId: string) => () => void;
}

export const ProductStockList: React.FC<ProductStockListProps> = ({
  productStocks,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onProductStockRowClick,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 8;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Mall" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Brand" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="SKU" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Product" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Variant" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Retail Price" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Availability Status" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Quantity" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {productStocks &&
            renderCollection(
              productStocks,
              productStock => (
                <TableRow
                  className={!!productStock ? classes.tableRow : undefined}
                  hover={!!productStock}
                  key={productStock ? productStock.id : "skeleton"}
                  onClick={
                    productStock
                      ? onProductStockRowClick(
                          btoa(`Product:${productStock.product_id}`),
                          productStock.is_simple_product
                            ? ""
                            : btoa(`ProductVariant:${productStock.variant_id}`)
                        )
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.mall_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.brand_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.sku,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.product_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.variant_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () =>
                        formatter.format(
                          maybe(() => productStock.price_amount, 0)
                        ),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.availability_status,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => productStock.quantity,
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No product stock found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default ProductStockList;
